import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import * as DeviceActions from './device.actions';
import { selectDeviceState } from './device.selectors';
import { mergeMap, map, catchError, filter, of, switchMap } from 'rxjs';
import { DeviceService } from '@core/services/device.service';

// How long a successful load is considered fresh before loadDevicesIfNeeded refetches.
const DEVICES_CACHE_TTL_MS = 60_000;

@Injectable()
export class DeviceEffects {
  constructor(
    private actions$: Actions,
    private deviceService: DeviceService,
    private store: Store
  ) {}

  // Store-first guard: skip the fetch when a load is already in flight or the cache is fresh.
  loadDevicesIfNeeded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeviceActions.loadDevicesIfNeeded),
      concatLatestFrom(() => this.store.select(selectDeviceState)),
      filter(([, state]) => !state.loading
        && (!state.loaded || Date.now() - (state.lastLoadedAt ?? 0) > DEVICES_CACHE_TTL_MS)),
      map(() => DeviceActions.loadDevices())
    )
  );

  // LOAD DEVICES
  loadDevices$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeviceActions.loadDevices),
      // switchMap so concurrent duplicate dispatches share one request instead of
      // racing (out-of-order responses previously did last-write-wins on the state).
      switchMap(() =>
        this.deviceService.loadAllDevices().pipe(
          map((devices) => DeviceActions.loadDevicesSuccess({ devices })),
          catchError((error) => of(DeviceActions.loadDevicesFailure({ error })))
        )
      )
    )
  );

  // REGISTER DEVICE
  registerDevices$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeviceActions.registerDevices),
      mergeMap(({ request }) =>
        this.deviceService.registerDevices(request).pipe(
          map((devices) =>
            DeviceActions.registerDevicesSuccess({ devices })
          ),
          catchError((error) => of(DeviceActions.registerDevicesFailure({ error })))
        )
      )
    )
  );

  // REMOVE DEVICE
  removeDevice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeviceActions.removeDevice),
      mergeMap(({ deviceId }) =>
        this.deviceService.removeDevice(deviceId).pipe(
          map((deletedId) =>
            DeviceActions.RemoveDeviceSuccess({ deviceId: deletedId })
          ),
          catchError((error) => of(DeviceActions.removeDeviceFailure({ error })))
        )
      )
    )
  );
}